import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_PATH } from '../../consts/app.const';
import { ICreatedTournamentPlayers, ICreatedTournamentTeams, ITournamentModel, ITournamentPlayerModel, ITournamentTeamModel } from './model/tournament.model';
import {
  ICreatedEntityDto,
  ICreatedTournamentPlayersDto,
  ICreatedTournamentTeamsDto,
  ICreateTournamentDto,
  ICreateTournamentPlayersDto,
  ICreateTournamentTeamsDto,
  ITournamentDto,
  ITournamentPlayersDto,
  ITournamentsDto,
  ITournamentTeamsDto,
  ITournamentTeamUpdateDto,
  IUpdateTournamentDto,
} from '@oogaday/shared';
import { UtilsService } from '../../services/utils.service';
import { ICreatedModel } from '../common/model/common.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TournamentApiService {
  private readonly httpClient = inject(HttpClient);


  getAvailableTournaments(): Promise<ITournamentModel[]> {
    return firstValueFrom(this.httpClient.get<ITournamentsDto>(`${API_PATH}tournaments`))
      .then(({ tournaments }) => tournaments.map(UtilsService.dtoToModel));
  }

  createTournament(data: ICreateTournamentDto): Promise<ICreatedModel> {
    return firstValueFrom(this.httpClient.post<ICreatedEntityDto>(`${API_PATH}tournaments`, data));
  }

  updateTournament(tourId: string, diff: IUpdateTournamentDto): Promise<void> {
    return firstValueFrom(this.httpClient.patch<void>(`${API_PATH}tournaments/${tourId}`, diff));
  }

  getTournament(tourId: string): Promise<ITournamentModel> {
    return firstValueFrom(this.httpClient.get<ITournamentDto>(`${API_PATH}tournaments/${tourId}`))
      .then(UtilsService.dtoToModel);
  }

  getTournamentTeams(tourId: string): Promise<ITournamentTeamModel[]> {
    return firstValueFrom(this.httpClient.get<ITournamentTeamsDto>(`${API_PATH}tournaments/${tourId}/teams`))
      .then(({ teams }) => teams.map(UtilsService.dtoToModel));
  }

  createTournamentTeams(tourId: string, data: ICreateTournamentTeamsDto): Promise<ICreatedTournamentTeams> {
    return firstValueFrom(this.httpClient.post<ICreatedTournamentTeamsDto>(`${API_PATH}tournaments/${tourId}/teams`, data));
  }

  getTournamentPlayers(tourId: string, teamIds: number[]): Promise<ITournamentPlayerModel[]> {
    return firstValueFrom(this.httpClient.get<ITournamentPlayersDto>(`${API_PATH}tournaments/${tourId}/players`, {
      params: {
        teamIds,
      },
    }))
      .then(({ players }) => players.map(UtilsService.dtoToModel));
  }

  createTournamentPlayers(tourId: string, data: ICreateTournamentPlayersDto): Promise<ICreatedTournamentPlayers> {
    return firstValueFrom(this.httpClient.post<ICreatedTournamentPlayersDto>(`${API_PATH}tournaments/${tourId}/players`, data));
  }

  deleteTournament(tournamentId: string): Promise<void> {
    return firstValueFrom(this.httpClient.delete<void>(`${API_PATH}tournaments/${tournamentId}`));
  }

  async updateTeam(tournamentId: string, teamId: number, tournamentTeamUpdateDto: ITournamentTeamUpdateDto): Promise<void> {
    await firstValueFrom(this.httpClient.patch<void>(`${API_PATH}tournaments/${tournamentId}/teams/${teamId}`, tournamentTeamUpdateDto));
  }

  getTournamentById(tourId: string): Promise<ITournamentModel> {
    return firstValueFrom(this.httpClient.get<ITournamentDto>(`${API_PATH}tournaments/${tourId}`))
      .then(UtilsService.dtoToModel);
  }
}
